import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { parseISO } from 'date-fns';
import TimelineItem from './TimelineItem';
import LocaleContext from './LocaleContext';
import { breakpoints } from '../styles/Variables';
import { tr } from '../utils/translations';

const TimelineStyles = styled.div`
  position: relative;

  .timelineControlsContainer {
    padding: 4rem var(--col1) 2rem var(--col1);
    margin-bottom: 2rem;
    position: relative;
    background-color: var(--grey);

    @media (min-width: ${breakpoints.breakLg}) {
      background-color: transparent;
      padding: 0;
    }
  }

  .timelineYears {
    @media (min-width: ${breakpoints.breakLg}) {
      display: flex;
      max-width: 54rem;
      margin: 0 auto;
      min-height: 12rem;
      align-items: top;
      position: relative;

      &::after {
        display: block;
        position: absolute;
        content: '';
        left: 0;
        bottom: 0;
        width: 100%;
        height: 0.5rem;
        border-top: 1px solid var(--black);
        border-bottom: 1px solid var(--black);
      }
    }

    .timelineYear {
      height: 100%;

      @media (min-width: ${breakpoints.breakLg}) {
        width: 33%;
      }

      .theYear {
        position: relative;

        &::before {
          content: '';
          display: block;
          position: absolute;
          left: 3rem;
          top: 40%;
          height: 1px;
          width: calc(100% - 3rem);
          background-color: var(--black);
        }

        @media (min-width: ${breakpoints.breakLg}) {
          position: absolute;
          bottom: -1.75em;
          height: calc(100% + 1.75em);

          .label {
            position: absolute;
            bottom: 0;
          }

          &::before {
            top: 0;
            left: 1rem;
            height: calc(100% - 1.75em);
            width: 1px;
          }

          &::after {
            content: '';
            display: block;
            position: absolute;
            bottom: 1.75em;
            left: 0.87rem;
            height: calc(0.5rem + 2px);
            width: 0.3rem;
            background: var(--black);
          }
        }
      }

      .timelineItemButtons {
        padding-left: 3rem;
        padding-bottom: 1rem;
        padding-top: 0.5rem;

        .timelineItemButton {
          display: block;
          margin-bottom: 1rem;

          button {
            text-transform: uppercase;
            border-color: var(--black);
            background-color: var(--offWhite);
            padding: 0.3rem 1rem 0.15rem 1rem;
          }

          &.selected {
            button {
              background-color: var(--black);
              color: var(--offWhite);
            }
          }

          @media (min-width: ${breakpoints.breakLg}) {
            margin-bottom: 0.5rem;
          }
        }

        @media (min-width: ${breakpoints.breakLg}) {
          padding-left: 2rem;
          padding-top: 0;
        }
      }
    }
  }

  .timelineItemsContainer {
    padding: 2rem var(--col1) 2rem var(--col1);

    .notice {
      font-style: italic;
      text-align: center;
    }

    @media (min-width: ${breakpoints.breakLg}) {
      padding-top: 4rem;
    }
  }

  .timelineItem {
    display: none;

    &.visible {
      display: block;
    }
  }
`;

export default function Timeline({ name, timelineItems, imageMaxWidth }) {
  const [currentItem, setCurrentItem] = useState();
  const [locale] = useContext(LocaleContext);
  const years = ['2022', '2023', '2024'];

  const goToTimelineItem = (className) => {
    setCurrentItem(className);

    // Scroll milestones into view
    const timelineItemsContainer = document.getElementById(
      'timelineItemsContainer'
    );
    const headerOffset = 45;
    const elementPosition = timelineItemsContainer.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  return (
    <TimelineStyles>
      <div className="timelineControlsContainer">
        <div className="timelineYears">
          {years.map((year) => (
            <div className="timelineYear" key={year}>
              <div className="theYear">
                <div className="label">{year}</div>
              </div>
              <div className="timelineItemButtons">
                {timelineItems.nodes.map((timelineItem) => {
                  const itemYear = timelineItem.date.split('-')[0];
                  if (year !== itemYear) return null;
                  return (
                    <div
                      key={timelineItem.className}
                      className={`timelineItemButton ${
                        currentItem === timelineItem.className ? 'selected' : ''
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => goToTimelineItem(timelineItem.className)}
                      >
                        {timelineItem.name}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="timelineItemsContainer" id="timelineItemsContainer">
        {!currentItem && (
          <p className="notice">{tr('notices', 'swipeTimeline', locale)}</p>
        )}
        {timelineItems.nodes.map((timelineItem) => {
          if (currentItem === timelineItem.className) {
            return (
              <TimelineItem
                timelineItem={timelineItem}
                key={timelineItem.className}
                imageMaxWidth={864} // ~54rem
              />
            );
          }
          return null;
        })}
      </div>
    </TimelineStyles>
  );
}
